import React, { useState, useEffect, useRef } from 'react'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import { RootState, PromptOptions, OrganizationData } from 'app/types'
import { Spinner, TourIFrame, BackButton, IconButton, Prompt } from 'components'
import { showPrompt, navigateBackAsync } from 'actions/appActions'
import { getTourUrl } from 'helpers/config'
import { showDisclaimer } from 'app/state'
import { logger } from 'helpers/logger'
import * as fnc from 'helpers/fnc'

interface ModelhomeViewProps {
    app?: AppData
    organization?: OrganizationData
    splitIndex: number
    mediaId: string
    dataLink: string
    disclaimer: boolean
    onClose: () => void
    onFullscreen: () => void
    fullscreen: boolean
    inPrompt: boolean
}

export function ModelhomeView(props: ModelhomeViewProps) {
    const { app, organization, mediaId, dataLink, splitIndex, disclaimer, onClose, onFullscreen, fullscreen, inPrompt } = { disclaimer: true, ...props }
    const dispatch = useAppDispatch()
    const media = useAppSelector((state: RootState) => state.app.media)
    const [url, setUrl] = useState(null)
    const [viewRef, setViewRef] = useState(null)
    const [loading, setLoading] = useState(0)
    const [showInlineDisclaimer, setShowInlineDisclaimer] = useState(false)
    const [disclaimerOptions, setDisclaimerOptions] = useState({ ...PromptOptions.ModelHomeDisclaimer })
    const [isExternal, setIsExternal] = useState(false)
    const [initialized, setInitialized] = useState(false)

    const isFullscreen = fullscreen || (viewRef != null && fnc.isFullscreen() == viewRef)

    // Determine which modelhomes array to use (app takes priority)
    const modelhomes = app?.modelhomes || organization?.modelhomes || []
    const contextType = app ? 'app' : organization ? 'organization' : 'none'

    useEffect(() => {
        let m = null
        let newUrl = null
        let newDisclaimerOptions = { ...disclaimerOptions }
        
        if (dataLink) {
            const allTourMedia = Object.values(media).filter((x) => x.link == dataLink)
            const appIds = [app?.id, app?.meta?.id].filter((x) => x != null)
            if (app) {
                m = allTourMedia.find((x) => appIds.includes(x.appId))
            } else if (organization) {
                m = allTourMedia.find((x) => x.organizationId == organization.id)
            } else {
                m = allTourMedia[0]
            }

            // If no strict context match exists, still use the single matching link as fallback.
            if (!m && allTourMedia.length == 1) {
                m = allTourMedia[0]
            }

            logger.info('[ModelhomeView] dataLink lookup:', dataLink, 'matches by link:', allTourMedia.length, 'final match:', !!m, 'app.id:', app?.id, 'app.meta.id:', app?.meta?.id)
            if (!m && allTourMedia.length > 0) {
                logger.info('[ModelhomeView] appId mismatch - media appIds:', allTourMedia.map((x) => x.appId), 'orgIds:', allTourMedia.map((x) => x.organizationId))
            }
            // Get disclaimer from the tour media itself, not from modelhome
        } else if (mediaId && mediaId in media) {
            m = media[mediaId]
        }

        if (m?.data) {
            newDisclaimerOptions.message = m.data
            newDisclaimerOptions.id = m.id
        }
        
        if (m) {
            if (m.link.includes('http')) {
                newUrl = m.link
            } else {
                newUrl = `${getTourUrl()}/${m.link}`
            }
        }
        
        setUrl(newUrl)
        setDisclaimerOptions(newDisclaimerOptions)
        setIsExternal(!newUrl?.includes('homegyde'))
        if (!newUrl) {
            logger.info('[ModelhomeView] no tour URL resolved', {
                dataLink,
                mediaId,
                appId: app?.id,
                appMetaId: app?.meta?.id,
                organizationId: organization?.id,
            })
        }
        setInitialized(true)
    }, [app, organization, dataLink, mediaId, media])

    // Debug: Log all window messages to identify events from older tour technology
    useEffect(() => {
        function handleAllMessages(e) {
            // Log all postMessage events to help identify what older tours send
            logger.info('[Tour Debug] Window message received:', {
                data: e.data,
                origin: e.origin,
                type: typeof e.data
            })
        }
        window.addEventListener('message', handleAllMessages)
        return () => {
            window.removeEventListener('message', handleAllMessages)
        }
    }, [])

    useEffect(() => {
        window.addEventListener('message', handleWindowEvent)
        return () => {
            window.removeEventListener('message', handleWindowEvent)
        }
    }, [])

    useEffect(() => {
        if (!initialized) {
            return
        }
        if (!url) {
            return
        }
        if (loading == 2 || isExternal) {
            if (disclaimer && (showDisclaimer('modelhome_' + disclaimerOptions.id) || disclaimerOptions?.alwaysShow)) {
                if (inPrompt) {
                    setShowInlineDisclaimer(true)
                } else {
                    dispatch(showPrompt(disclaimerOptions))
                }
            } else {
            }
        }
    }, [initialized, loading, isExternal, disclaimerOptions, url])

    function handleWindowEvent(e) {
        // Check for various possible tour loaded events
        const data = e.data
        
        // Handle known 'tourLoaded' event
        if (data == 'tourLoaded') {
            handleTourReady()
            return
        }
        
        // Check for other possible events from older tour technologies
        // Add more conditions here as you discover them from the debug logs
        if (typeof data === 'object') {
            // Some tours might send objects with type/event properties
            if (data.type === 'ready' || data.event === 'ready' || data.loaded === true) {
                logger.info('[Tour] Detected ready event from tour:', data)
                handleTourReady()
                return
            }
        }
        
        if (typeof data === 'string') {
            // Check for other string-based events
            const lowerData = data.toLowerCase()
            if (lowerData.includes('ready') || lowerData.includes('loaded') || lowerData.includes('complete')) {
                logger.info('[Tour] Detected potential ready event:', data)
                handleTourReady()
                return
            }
        }
    }
    
    function handleTourReady() {
        if (loading >= 1) return // Already loading or loaded
        setTimeout(() => {
            setLoading(1)
            setTimeout(() => {
                setLoading(2)
            }, 250)
        }, 700)
    }
    
    // Fallback: Use iframe onLoad event for tours that don't send postMessage events
    function handleIframeLoad() {
        logger.info('[Tour] Iframe onLoad fired, isExternal:', isExternal)
        // For external tours or tours that don't send postMessage, use iframe load as fallback
        if (isExternal || loading === 0) {
            // Give a bit more time for the tour content to initialize after iframe loads
            setTimeout(() => {
                if (loading === 0) {
                    logger.info('[Tour] Using iframe onLoad fallback for tour loading')
                    handleTourReady()
                }
            }, 1500) // Wait 1.5s after iframe load before falling back
        }
    }

    function handleClose() {
        if (onClose) {
            onClose()
        }
    }
    function handleFullscreen() {
        if (onFullscreen && viewRef) {
            onFullscreen(viewRef)
        }
    }

    if (!url) {
        return null
    }
    return <div id="modelhome-view" ref={setViewRef}>
        {showInlineDisclaimer && <Prompt prompt={disclaimerOptions} onDismiss={() => setShowInlineDisclaimer(false)} />}
        {!isExternal && loading < 2 && <Spinner invert overlay show={loading == 0} showAfter={500} quotesAfter={500} />}
        <TourIFrame src={url} onLoad={handleIframeLoad} />
        {(onClose || onFullscreen) && <div className="top-right">
            {onFullscreen && <IconButton icon={isFullscreen ? "fas fa-compress-alt" : "fas fa-expand-alt"} onClick={handleFullscreen} />}
            {onClose && <IconButton icon="fas fa-times" onClick={handleClose} />}
        </div>}
    </div>
}
